/* istanbul ignore file */
import { FC, ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { Outlet } from "react-router";
import { useTasks } from "../../api/hooks/use-tasks";
import { useApproveTasks } from "../../api/hooks/use-approve-tasks";
import { isRejected } from "./utils/is-rejected";
import { isApproved } from "./utils/is-approved";
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import {
  TasksListResponse,
  RemediationTask,
  TaskExecutionStatus,
} from "stan-client";
import { useRejectTasks } from "../../api/hooks/use-reject-tasks";
import { useSelectable } from "../../hooks/use-selectable";
import { useQueryParamState } from "@backstage/core-components";
import { SortOption } from "../logs/utils/parse-sort-param";
import { UiSettings } from "../../constants/ui";
import { identityApiRef, useApi } from "@backstage/core-plugin-api";
import { parseNumber } from "../../utils/parse-number";
import { pageToSkip } from "../../utils/pagination";

export type EscalationParam = "all" | "escalated" | "on_time";
export type StatusParam = TaskExecutionStatus | "all";

type ContextType = {
  tasksQuery: UseQueryResult<TasksListResponse, Error>;
  approveTasks: UseMutationResult<void, Error, string[], unknown>;
  rejectTasks: UseMutationResult<void, Error, string[], unknown>;
  tasks: RemediationTask[];
  selected: Map<string, boolean>;
  toggleTasks: (ids: string[]) => void;
  setAllTasksTo: (value: boolean) => void;
  isAllSelected: boolean;
  totalTasks: number;
  totalRejected: number;
  totalApproved: number;
  totalSelectedTasks: number;
  selectedTasks: string[];
  params: {
    limit: number;
    setLimit: (n: number) => void;
    page: number;
    setPage: (n: number) => void;
    sort: SortOption | undefined;
    setSort: (n: SortOption) => void;
    assignee: "me" | "all" | undefined;
    setAssignee: (n: "me" | "all") => void;
    escalation: EscalationParam | undefined;
    setEscalation: (n: EscalationParam) => void;
    status: StatusParam | undefined;
    setStatus: (n: StatusParam) => void;
  };
};

const Context = createContext<ContextType | null>(null);

export const useTasksContext = () => {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error("TasksContextProvider missing");
  }
  return ctx;
};

const parseEscalation = (param?: EscalationParam) => {
  switch (param) {
    case "escalated":
      return true;
    case "on_time":
      return false;
    default:
      return undefined;
  }
};

const parseStatusParam = (param?: StatusParam) => {
  if (!param || param === "all") {
    return undefined;
  }
  return param;
};

type TasksContextProviderProps = {
  children: ReactNode;
};

const resolveAssignee = (p?: "me" | "all"): "me" | "all" => p ?? "me";
const resolveSort = (p?: SortOption): SortOption => p ?? "newest";
const resolveEscalation = (p?: EscalationParam): EscalationParam => p ?? "all";
const resolveStatus = (p?: StatusParam): StatusParam => p ?? "all";
const buildQueryOrder = (sort: SortOption): "asc" | "desc" =>
  sort === "newest" ? "desc" : "asc";
const getAssigneeEmail = (
  assignee: "me" | "all",
  email?: string
): string | undefined => (assignee === "me" ? email : undefined);
const isQueryEnabled = (param?: "me" | "all", email?: string) =>
  param === "all" || !!email;
const resolveTasks = (data?: TasksListResponse) =>
  (data?.tasks ?? []) as (RemediationTask & { id: string })[];
const resolveTotal = (data?: TasksListResponse) => data?.total ?? 0;

export const TasksContextProvider: FC<TasksContextProviderProps> = ({
  children,
}) => {
  const [limitParam, setLimit] = useQueryParamState<number>("limit");

  const [pageParam, setPage] = useQueryParamState<number>("page");
  const [sortParam, setSort] = useQueryParamState<SortOption>("sort");
  const [assigneeParam, setAssignee] = useQueryParamState<"me" | "all">(
    "assignee"
  );
  const [statusParam, setStatus] = useQueryParamState<StatusParam>("status");
  const [escalationParam, setEscalation] =
    useQueryParamState<EscalationParam>("escalation");

  const limit = parseNumber(limitParam, UiSettings.TableDefaultLimit);
  const page = parseNumber(pageParam, UiSettings.TableDefaultPage) - 1;

  const identityApi = useApi(identityApiRef);
  const [email, setEmail] = useState<string | undefined>();

  useEffect(() => {
    identityApi.getProfileInfo().then((profile) => {
      setEmail(profile.email);
    });
  }, [identityApi]);

  const assignee = resolveAssignee(assigneeParam);
  const sort = resolveSort(sortParam);

  const params = {
    limit,
    setLimit,
    page,
    setPage,
    sort,
    setSort,
    assignee,
    setAssignee,
    escalation: resolveEscalation(escalationParam),
    setEscalation,
    status: resolveStatus(statusParam),
    setStatus,
  };

  const tasksQuery = useTasks(
    {
      escalated: parseEscalation(escalationParam),
      execution_status: parseStatusParam(statusParam),
      limit,
      offset: pageToSkip(page, limit),
      order: buildQueryOrder(sort),
      assignee_email: getAssigneeEmail(assignee, email),
    },
    {
      enabled: isQueryEnabled(assigneeParam, email),
    }
  );

  const tasks = resolveTasks(tasksQuery.data);

  const {
    selected,
    deleteFromSelected,
    toggleSelected,
    setAllSelectedTo,
    totalSelected,
    selectedItems,
  } = useSelectable(tasks);

  const totalTasks = resolveTotal(tasksQuery.data);
  const rejectedTasks = tasks.filter((x) => isRejected(x.approval_status));
  const approvedTasks = tasks.filter((x) => isApproved(x.approval_status));

  const openTasksFilter = ({ approval_status }: RemediationTask) =>
    !isRejected(approval_status) && !isApproved(approval_status);
  const openTasks = tasks.filter(openTasksFilter);

  const isAllSelected =
    openTasks.length !== 0 &&
    openTasks.length === [...selected.values()].filter((x) => !!x).length;

  const setAllTasksTo = (value: boolean) => {
    setAllSelectedTo(value, openTasksFilter);
  };

  const approveTasks = useApproveTasks({
    onSuccess: (_data, ids) => {
      deleteFromSelected(ids);
    },
  });
  const rejectTasks = useRejectTasks({
    onSuccess: (_data, ids) => {
      deleteFromSelected(ids);
    },
  });

  return (
    <Context.Provider
      value={{
        tasksQuery,
        approveTasks,
        rejectTasks,
        tasks,
        selected,
        toggleTasks: toggleSelected,
        isAllSelected,
        setAllTasksTo,
        totalTasks,
        totalRejected: rejectedTasks.length,
        totalApproved: approvedTasks.length,
        totalSelectedTasks: totalSelected,
        selectedTasks: selectedItems,
        params,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const TasksLayout: FC = () => (
  <TasksContextProvider>
    <Outlet />
  </TasksContextProvider>
);
